/* eslint-disable @typescript-eslint/no-explicit-any */
import Skeleton from 'components/Skeleton/Skeleton';
import { useMemo } from 'react';
import { FormRenderProps, RenderableProps } from 'react-final-form';
import { makeEditFormFunc } from 'utils/api';
import { FetchCallback } from 'utils/fetch';
import arrayMutators from 'final-form-arrays';
import { Form, FormContainerMainProps } from 'ui/FormContainer/FormContainer';
import { useQuery } from '@tanstack/react-query';
import { NotFoundPageContent } from 'pages/errors/404';

type EditFormProps<
    FormValues extends Record<string, any> = Record<string, any>
> = RenderableProps<FormRenderProps<FormValues>> & {
    // Функция получения значений полей формы (обычно обертка обращения к API с передачей ID объекта)
    getter?(): Promise<FormValues>;
    // Компонент, который отображается во время загрузки первичных значений полей
    skeleton?: React.ReactNode;
    // Сообщение об успешности отправки
    successMessage?: string;
    // Функция сохранения данных (обычно обращение к API)
    saver(data: FormValues): FetchCallback<any, FormValues>;

    initialValues?: Partial<FormValues>;

    queryKey?: (string | number)[];
} & FormContainerMainProps;

export function EditForm<FormValues extends Record<string, any>>({
    getter,
    skeleton,
    saver,
    successMessage,
    initialValues: rawValues,
    queryKey = [],
    ...props
}: EditFormProps<FormValues>): JSX.Element {
    const { data, error, isLoading } = useQuery({
        queryKey: ['edit', ...queryKey],
        queryFn: () => getter?.(),
        enabled: !!getter
    });

    const submitFunc = useMemo(
        () => makeEditFormFunc(saver, successMessage),
        [saver, successMessage]
    );

    if (isLoading) {
        return <>{skeleton || <Skeleton />}</>;
    }

    if (error) {
        return <NotFoundPageContent />;
    }

    return (
        <Form
            onSubmit={submitFunc}
            initialValues={data ?? rawValues}
            mutators={{ ...arrayMutators }}
            {...props}
        />
    );
}
